import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import logoutFetch from '../../../../assets/helpers/logoutFetch';
import { Context } from '../../../../context';
import { useFacilitatorStore } from '../../useFacilitatorStore';
import { useAdminProgressQuery } from '../../useAdminProgressQuery';
import { hasFullFacilitatorProgramAccessRole } from '../../../../utils/authRole';

const FACILITATOR_TECH_SUPPORT_URL =
  'https://osu.az1.qualtrics.com/jfe/form/SV_eqy7DOElHo3rYh0';

const LinkIcon = () => (
  <svg
    aria-hidden='true'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 1086.24 1086.24'
  >
    <path d='M1058.43,6.04c2.47,1.08,6.52,3.97,8.56,5.7,1.39,1.08,2.69,2.27,3.94,3.49l.06.09c1.22,1.22,2.41,2.55,3.54,3.91.82,1.05,1.5,1.9,2.27,3h.03c.71,1.02,1.33,2.1,2.04,3.12l.74,1.39h.03l1.19,2.32c1.64,3.34,2.98,6.92,3.88,10.63.99,4.08,1.53,8.22,1.53,12.33v374.26c0,14.37-5.84,27.38-15.25,36.79l-1.05.99c-9.33,8.82-21.91,14.26-35.72,14.26s-27.33-5.84-36.74-15.25l-.06-.06c-9.41-9.41-15.25-22.42-15.25-36.74v-248.66L443.91,715.89c-10.15,10.15-23.47,15.22-36.77,15.22s-26.62-5.07-36.79-15.22c-10.15-10.18-15.22-23.47-15.22-36.79s5.07-26.62,15.22-36.77L908.62,104.03h-245.23c-14.37,0-27.38-5.81-36.79-15.22l-.99-1.08c-8.82-9.33-14.23-21.91-14.23-35.72s5.81-27.3,15.22-36.71v-.06c9.41-9.41,22.42-15.25,36.79-15.25h370.83c4.11,0,8.25.54,12.33,1.53,4.11.99,8.02,2.52,11.71,4.42l.17.08ZM762.83,526.79c0-14.34,5.84-27.38,15.25-36.79l1.08-.96c9.33-8.84,21.88-14.26,35.72-14.26s27.35,5.81,36.77,15.22c9.41,9.41,15.25,22.45,15.25,36.79v507.43c0,14.32-5.84,27.3-15.25,36.71v.06c-9.41,9.41-22.42,15.25-36.77,15.25H52.02c-14.34,0-27.35-5.84-36.77-15.25l-.99-1.08c-8.82-9.3-14.26-21.88-14.26-35.69V271.36c0-14.34,5.84-27.35,15.25-36.77l1.08-.99c9.35-8.84,21.91-14.26,35.69-14.26h505.45c14.31,0,27.33,5.84,36.77,15.28l.03-.03c9.41,9.41,15.25,22.42,15.25,36.77s-5.84,27.38-15.25,36.79l-1.08.99c-9.35,8.82-21.94,14.26-35.72,14.26H104.03v658.77h658.8v-455.39Z' />
  </svg>
);

const baseButtonClassName =
  'inline-flex min-w-[148px] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold no-underline transition-colors duration-200';
export const disabledActionAppearanceClassName =
  'cursor-not-allowed border-slate-300 bg-slate-100 text-slate-500 pointer-events-none';
const disabledButtonClassName = `${baseButtonClassName} ${disabledActionAppearanceClassName}`;
const supportButtonClassName = `${baseButtonClassName} border-[#5f2c86] bg-white text-[#5f2c86] hover:bg-[#f3ebfb] hover:text-[#4b236b]`;
const logoutButtonClassName = `${baseButtonClassName} border-light-blue bg-white text-light-blue hover:border-light-blue hover:bg-light-blue hover:text-white`;
const dashboardButtonClassName = `${baseButtonClassName} border-dark-blue bg-white text-dark-blue hover:bg-dark-blue hover:text-white`;
const ezParentButtonClassName = `${baseButtonClassName} border-dark-blue bg-white text-dark-blue hover:bg-dark-blue hover:text-white`;
const FACILITATOR_PARENT_PREVIEW_ROUTE = '/facilitator/parent-preview';

const DisabledProductButton = ({ children }) => (
  <button type='button' disabled className={disabledButtonClassName}>
    <span>{children}</span>
    <span className='w-4 fill-slate-400'>
      <LinkIcon />
    </span>
  </button>
);

const FacilitatorHeaderActions = ({ isMobile = false }) => {
  const context = useContext(Context);
  const { adminProgress, hasLoadedAdminProgress } = useAdminProgressQuery({
    currentPath: '',
  });
  const hasFullFacilitatorProgramAccess =
    hasFullFacilitatorProgramAccessRole(localStorage.getItem('role'));
  const dashboardLabelPrefix = hasFullFacilitatorProgramAccess
    ? 'Admin'
    : 'Facilitator';
  const canAccessEzParent =
    hasLoadedAdminProgress &&
    (hasFullFacilitatorProgramAccess ||
      adminProgress.postSurveyCompleted === true);
  const canAccessDashboard =
    hasLoadedAdminProgress &&
    (hasFullFacilitatorProgramAccess ||
      adminProgress.postSurveyCompleted === true);

  const handleLogout = () => {
    logoutFetch();
    context.logout();
    useFacilitatorStore.getState().resetStore();
    localStorage.removeItem('facilitator-storage');
  };

  return (
    <div
      className={`flex gap-3 ${
        isMobile ? 'w-full flex-col [&>*]:w-full' : 'items-center'
      }`}
    >
      {canAccessEzParent ? (
        <Link
          to={FACILITATOR_PARENT_PREVIEW_ROUTE}
          target='_blank'
          rel='noreferrer'
          className={ezParentButtonClassName}
        >
          <span>
            <i>ez</i>Parent
          </span>
          <span className='w-4 fill-current'>
            <LinkIcon />
          </span>
        </Link>
      ) : (
        <DisabledProductButton>
          <i>ez</i>Parent
        </DisabledProductButton>
      )}
      {canAccessDashboard ? (
        <Link to='/facilitator/dashboard' className={dashboardButtonClassName}>
          <span>
            {dashboardLabelPrefix} <i>ez</i>Dashboard
          </span>
          <span className='w-4 fill-current'>
            <LinkIcon />
          </span>
        </Link>
      ) : (
        <DisabledProductButton>
          <>
            {dashboardLabelPrefix} <i>ez</i>Dashboard
          </>
        </DisabledProductButton>
      )}
      <a
        href={FACILITATOR_TECH_SUPPORT_URL}
        target='_blank'
        rel='noreferrer'
        className={supportButtonClassName}
      >
        Need Tech Support?
      </a>
      <button
        type='button'
        className={logoutButtonClassName}
        onClick={handleLogout}
      >
        Log Out
      </button>
    </div>
  );
};

export default FacilitatorHeaderActions;
